/* =====================================================================================
   The clinicians at a centre.

   crm.persons and crm.person_affiliations already existed and were empty. They were built for
   exactly this, and the design deserves saying out loud, because it is the part that keeps this
   lawful: persons carries lawful_basis, privacy_notice_sent_at, objection_received_at,
   objection_scope, erasure_requested_at, restrict_processing and retention_review_due, and every
   affiliation carries a source_url and a confidence.

   A CLINICIAN'S WORK EMAIL IS PERSONAL DATA. Being professional and published does not change
   that under UK GDPR, it only makes legitimate interests an available basis rather than consent.
   Three consequences are enforced here rather than left to whoever loads the data:

     1. Nothing is shown without a source. A contact with no provenance is a rumour, and a rumour
        that reaches a real person's inbox is the kind of mistake that gets a complaint.
     2. status 'do_not_contact', restrict_processing and an erasure request suppress the record
        in the UI, not just in a report somewhere.
     3. Article 14 requires telling somebody you hold their data when you did not get it from
        them. privacy_notice_sent_at is surfaced so an unnotified contact is visible rather than
        quietly used.
   ===================================================================================== */

import { BACKEND } from './db';
import { dbSiteId } from './ids';

export interface SitePerson {
  id: string;
  full_name: string;
  title: string | null;
  credentials: string | null;
  job_title: string | null;
  specialties: string[] | null;
  organ_focus: string[] | null;
  work_email: string | null;
  work_phone: string | null;
  status: string;
  lawful_basis: string | null;
  privacy_notice_sent_at: string | null;
  restrict_processing: boolean;
  erasure_requested_at: string | null;
  data_source: string | null;
  /* From the affiliation, not the person: the same clinician can hold a different role at a
     second centre, which is common and is the reason the two are separate tables. */
  role_title: string | null;
  department: string | null;
  is_primary: boolean;
  confidence: string | null;
  source_url: string | null;
  started_on: string | null;
  ended_on: string | null;
}

export async function fetchSitePeople(seedSiteId: string): Promise<SitePerson[]> {
  if (BACKEND !== 'supabase') return [];
  const { supabase } = await import('./supabase');
  const siteId = dbSiteId(seedSiteId);

  const { data, error } = await supabase().schema('crm').from('person_affiliations')
    .select('role_title, department, is_primary, confidence, source_url, started_on, ended_on,'
          + ' persons:person_id (id, full_name, title, credentials, job_title, specialties,'
          + ' organ_focus, work_email, work_phone, status, lawful_basis, privacy_notice_sent_at,'
          + ' restrict_processing, erasure_requested_at, data_source)')
    .eq('site_id', siteId)
    .is('ended_on', null);
  if (error) throw new Error(error.message);

  return ((data ?? []) as unknown as Record<string, any>[])
    .filter((r) => r.persons)
    .map((r) => ({ ...r.persons, role_title: r.role_title, department: r.department,
                   is_primary: r.is_primary, confidence: r.confidence,
                   source_url: r.source_url, started_on: r.started_on, ended_on: r.ended_on }))
    /* Suppressed, not merely filtered from a report. A record under an erasure request or a
       processing restriction must not be displayed at all, and do_not_contact exists so somebody
       who has objected is not simply deleted and re-imported by the next refresh. */
    .filter((p: SitePerson) => !p.erasure_requested_at && !p.restrict_processing
                            && p.status !== 'do_not_contact')
    .sort((a: SitePerson, b: SitePerson) =>
      Number(b.is_primary) - Number(a.is_primary) || a.full_name.localeCompare(b.full_name, 'en'));
}


/* ---------------------------------------------------------------- adding one by hand

   The PubMed mapping finds clinicians who PUBLISH, which is a minority and skews academic. The
   ones met at a congress or on a call are the more useful half and there was no way to record
   them, so the only route into this table was a quarterly batch job.

   THE NAME IS THREE FIELDS, not one. crm.persons.full_name is GENERATED from title, first_name
   and last_name, so it cannot be written, and splitting a typed name on the last space gets
   "van der Berg", "de Marco" and "Castaneda-Puglianini" wrong. Asking is cheaper than guessing.

   A PERSON MAY ALREADY EXIST. The same clinician may have arrived from PubMed, or from another
   centre. Upserting on the identity key links them to this site instead of creating a second
   record, which is the fault that migration 0051 exists to prevent. */
export interface NewPerson {
  title?: string; firstName: string; lastName: string;
  jobTitle?: string; roleTitle?: string; department?: string;
  email?: string; phone?: string; organFocus?: string[];
}

export async function addSitePerson(seedSiteId: string, p: NewPerson): Promise<string> {
  if (BACKEND !== 'supabase') throw new Error('Adding a contact needs the database.');
  if (!p.firstName.trim() || !p.lastName.trim()) throw new Error('A first and last name.');
  const { supabase } = await import('./supabase');
  /* A function, not an upsert. The identity key is an EXPRESSION index and the affiliation key is
     PARTIAL, and PostgREST can infer neither from a column list: the upsert fails with an error
     about a missing constraint that sends you looking at the columns rather than the index type.
     Doing it server side is also atomic. */
  const { data, error } = await supabase().schema('crm').rpc('add_site_person', {
    p_site_id: dbSiteId(seedSiteId),
    p_first_name: p.firstName.trim(),
    p_last_name: p.lastName.trim(),
    p_title: p.title?.trim() || null,
    p_job_title: p.jobTitle?.trim() || null,
    p_role_title: p.roleTitle?.trim() || null,
    p_department: p.department?.trim() || null,
    p_email: p.email?.trim() || null,
    p_phone: p.phone?.trim() || null,
    p_organ_focus: p.organFocus ?? [],
  });
  if (error) throw new Error(error.message);
  return data as string;
}
